import React, { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { ReactComponent as CopyIcon } from "../../assets/icons/copy-icon.svg";
import { ReactComponent as CheckIcon } from "../../assets/icons/check-icon.svg";
import { ReactComponent as InfoCircleIcon } from "../../assets/icons/info-circle-icon.svg";
import { ReactComponent as AibexColorSideLogo } from "../../assets/icons/aibex-side-logo-color.svg";
import { ReactComponent as IdeaIcon } from "../../assets/icons/icon-idea.svg";
import ExternalMarkdownLink from "../ExternalMarkdownLink";

import styles from "../../assets/styles/components/ChatPanel.module.scss";
import type { Message } from "../../types/chat";

const normalizeMarkdownText = (text?: string) => (text || "").replace(/\\n/g, "\n");

interface MessageItemProps {
  msg: Message;
  onShowRef?: (msg: Message) => void;
  hideIcon?: boolean;
  isConsecutive?: boolean;
  isOnboarding?: boolean;
}

const MessageItem: React.FC<MessageItemProps> = ({ msg, onShowRef, hideIcon = false, isConsecutive = false, isOnboarding = false }) => {
  const [copied, setCopied] = useState(false);
  const [displayedText, setDisplayedText] = useState("");

  useEffect(() => {
    if (msg.sender === "bot" && msg.animate && msg.text) {
      let i = 0;
      setDisplayedText("");
      const timer = setInterval(() => {
        if (i < msg.text!.length) {
          const nextChar = msg.text![i];
          setDisplayedText((prev) => prev + (nextChar || ""));
          i++;
        } else {
          clearInterval(timer);
        }
      }, 15);
      return () => clearInterval(timer);
    } else {
      setDisplayedText(msg.text || "");
    }
  }, [msg.text, msg.animate, msg.sender]);

  const handleCopy = () => {
    navigator.clipboard.writeText(msg.text || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isHtml = msg.sender === "bot" && !!msg.text && msg.text.includes('class="');

  return (
    <div className={`${styles.messageWrapper} ${msg.sender === "user" ? styles.alignUser : styles.alignBot} ${isConsecutive ? styles.consecutive : ""}`}>
      {msg.sender === "bot" && (
        <div className={styles.botIconWrapper} style={{ visibility: hideIcon ? "hidden" : "visible" }}>
          <AibexColorSideLogo className={styles.botIcon} />
        </div>
      )}

      <div className={`${styles.message} ${styles[msg.sender]} ${msg.isLoading ? styles.loadingMessage : ""} ${isOnboarding && msg.sender === "bot" ? styles.onboardingBubble : ""}`}>
        {msg.isLoading ? (
          <>
            <div className={styles.typingAnimation}>
              <span />
              <span />
              <span />
            </div>
            <div className={styles.loadingText}>
              {displayedText ? (
                <div className="markdown-body">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: ExternalMarkdownLink }}>
                    {normalizeMarkdownText(displayedText)}
                  </ReactMarkdown>
                </div>
              ) : (
                "AIが回答を生成中..."
              )}
            </div>
          </>
        ) : isHtml ? (
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(displayedText as string, {
                ALLOWED_TAGS: ["b", "i", "em", "strong", "br", "p", "ul", "ol", "li", "a", "span", "div"],
                ALLOWED_ATTR: ["href", "target", "rel", "class", "style"],
              }),
            }}
          />
        ) : msg.sender === "bot" ? (
          <div className="markdown-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: ExternalMarkdownLink }}>
              {normalizeMarkdownText(displayedText)}
            </ReactMarkdown>
          </div>
        ) : (
          <div className={styles.userMessageContent}>
            {msg.aiSuggestionMode && (
              <div className={styles.modeHeader}>
                <IdeaIcon className={styles.modeIcon} aria-hidden="true" />
                <span>提案モード</span>
              </div>
            )}
            <div className={styles.userMessageMarkdown}>
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: ExternalMarkdownLink }}>
                {normalizeMarkdownText(displayedText)}
              </ReactMarkdown>
            </div>
          </div>
        )}

        {msg.sender === "bot" && !msg.isLoading && msg.text !== "提案を生成します。少々お待ちください..." && msg.text !== "提案の生成が完了しました！右側のAI提案をご確認ください！" && msg.text !== "提案の生成が完了しました！サイドバーのAI提案をご確認ください！" && onShowRef && (
          <div className={styles.messageActions}>
            <button
              type="button"
              className={styles.actionBtn}
              onClick={handleCopy}
              title="回答をコピー"
            >
              {copied ? (
                <CheckIcon width="14" height="14" />
              ) : (
                <CopyIcon width="14" height="14" />
              )}
            </button>
            <button
              type="button"
              className={styles.actionBtn}
              onClick={() => onShowRef(msg)}
              title="参照データを表示"
            >
              <InfoCircleIcon width="14" height="14" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageItem;
